/**
 * POOL BALANCE PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Features:
 * - Current pool balance overview (USD & PKR)
 * - Monthly pool history with opening/closing balances
 * - Add/edit monthly pool entries
 * - Exchange rate tracking
 * - Visual breakdown of income vs expenses
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Wallet,
  DollarSign,
  TrendingUp,
  Plus,
  Edit3,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Coins,
  ChevronRight
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { SkeletonTable, SkeletonStatCard } from '../../components/ui/Skeleton'
import { apiClient } from '../../lib/api'

// Animation configuration
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const tableRowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

interface PoolBalance {
  id?: number
  year: number
  month: string
  usd_opening: number
  usd_income: number
  usd_expenses: number
  usd_closing: number
  pkr_opening: number
  pkr_income: number
  pkr_expenses: number
  pkr_closing: number
  exchange_rate: number
  notes: string | null
  created_at?: string
  updated_at?: string
}

interface CurrentPool {
  usd_closing: number
  pkr_closing: number
  exchange_rate: number
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

export function PoolBalancePage() {
  const [loading, setLoading] = useState(true)
  const [poolHistory, setPoolHistory] = useState<PoolBalance[]>([])
  const [currentPool, setCurrentPool] = useState<CurrentPool>({
    usd_closing: 0,
    pkr_closing: 0,
    exchange_rate: 280
  })
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedPool, setSelectedPool] = useState<PoolBalance | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    month: MONTHS[new Date().getMonth()],
    usd_income: 0,
    usd_expenses: 0,
    pkr_income: 0,
    pkr_expenses: 0,
    exchange_rate: 280,
    notes: ''
  })

  useEffect(() => {
    fetchPoolData()
  }, [selectedYear])

  const fetchPoolData = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(`/financials/pool?year=${selectedYear}`)
      if (response.success) {
        setPoolHistory(response.data.history || [])
        setCurrentPool(response.data.current || {
          usd_closing: 0,
          pkr_closing: 0,
          exchange_rate: 280
        })
      }
    } catch (err) {
      console.error('Failed to fetch pool data:', err)
      setError('Failed to load pool data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleAddPool = async () => {
    try {
      setSaving(true)
      setError(null)
      const response = await apiClient.post('/financials/pool', formData)
      if (response.success) {
        await fetchPoolData()
        setIsAddModalOpen(false)
        resetForm()
      }
    } catch (err: any) {
      console.error('Failed to add pool entry:', err)
      setError(err.message || 'Failed to add pool entry')
    } finally {
      setSaving(false)
    }
  }

  const handleEditPool = async () => {
    if (!selectedPool) return

    try {
      setSaving(true)
      setError(null)
      const response = await apiClient.post('/financials/pool', {
        year: selectedPool.year,
        month: selectedPool.month,
        usd_income: formData.usd_income,
        usd_expenses: formData.usd_expenses,
        pkr_income: formData.pkr_income,
        pkr_expenses: formData.pkr_expenses,
        exchange_rate: formData.exchange_rate,
        notes: formData.notes
      })
      if (response.success) {
        await fetchPoolData()
        setIsEditModalOpen(false)
        resetForm()
      }
    } catch (err: any) {
      console.error('Failed to update pool entry:', err)
      setError(err.message || 'Failed to update pool entry')
    } finally {
      setSaving(false)
    }
  }

  const openEditModal = (pool: PoolBalance) => {
    setSelectedPool(pool)
    setFormData({
      year: pool.year,
      month: pool.month,
      usd_income: pool.usd_income,
      usd_expenses: pool.usd_expenses,
      pkr_income: pool.pkr_income,
      pkr_expenses: pool.pkr_expenses,
      exchange_rate: pool.exchange_rate,
      notes: pool.notes || ''
    })
    setIsEditModalOpen(true)
  }

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      month: MONTHS[new Date().getMonth()],
      usd_income: 0,
      usd_expenses: 0,
      pkr_income: 0,
      pkr_expenses: 0,
      exchange_rate: 280,
      notes: ''
    })
    setSelectedPool(null)
  }

  const formatCurrency = (amount: number, currency: 'USD' | 'PKR') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(amount)
    }
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Calculate totals for the year
  const yearTotals = poolHistory.reduce((acc, pool) => ({
    usd_income: acc.usd_income + Number(pool.usd_income || 0),
    usd_expenses: acc.usd_expenses + Number(pool.usd_expenses || 0),
    pkr_income: acc.pkr_income + Number(pool.pkr_income || 0),
    pkr_expenses: acc.pkr_expenses + Number(pool.pkr_expenses || 0)
  }), { usd_income: 0, usd_expenses: 0, pkr_income: 0, pkr_expenses: 0 })

  // Loading state
  if (loading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-56 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
            <div className="h-4 w-72 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-24 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
            <div className="h-10 w-40 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
          </div>
        </div>
        <StatCardGrid columns={4}>
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </StatCardGrid>
        <SkeletonTable rows={6} />
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            Pool Balance
          </h1>
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Manage dual currency pools (USD & PKR) with monthly tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={fetchPoolData}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Monthly Entry
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-red/20 bg-red/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-red/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red" />
                </div>
                <div className="flex-1">
                  <p className="text-red font-medium">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Current Balance Stats */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={4}>
          <StatCard
            title="Current USD Balance"
            value={formatCurrency(currentPool.usd_closing, 'USD')}
            icon={DollarSign}
            color="blue"
            badge={<Badge variant="blue">USD Pool</Badge>}
          />
          <StatCard
            title="Current PKR Balance"
            value={formatCurrency(currentPool.pkr_closing, 'PKR')}
            icon={Wallet}
            color="green"
            badge={<Badge variant="green">PKR Pool</Badge>}
          />
          <StatCard
            title="Exchange Rate"
            value={currentPool.exchange_rate.toFixed(2)}
            icon={TrendingUp}
            color="purple"
            badge={<Badge variant="purple">Wise Rate</Badge>}
          />
          <StatCard
            title="Total Value (PKR)"
            value={formatCurrency(
              currentPool.pkr_closing + (currentPool.usd_closing * currentPool.exchange_rate),
              'PKR'
            )}
            icon={Coins}
            color="orange"
            badge={<Badge variant="orange">Combined</Badge>}
          />
        </StatCardGrid>
      </motion.div>

      {/* Year Summary */}
      <motion.div variants={itemVariants} className="mt-6">
        <GlassCard>
          <div className="p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                Year {selectedYear} Summary
              </h2>
              <Select
                value={selectedYear.toString()}
                onChange={(value) => setSelectedYear(parseInt(value))}
                options={[
                  { value: '2024', label: '2024' },
                  { value: '2025', label: '2025' },
                  { value: '2026', label: '2026' }
                ]}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* USD Income */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-green/10 to-green/5 rounded-2xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-4 h-4 text-green" />
                  <span className="text-footnote font-medium text-green">USD Income</span>
                </div>
                <div className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                  {formatCurrency(yearTotals.usd_income, 'USD')}
                </div>
              </motion.div>

              {/* USD Expenses */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-red/10 to-red/5 rounded-2xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="w-4 h-4 text-red" />
                  <span className="text-footnote font-medium text-red">USD Expenses</span>
                </div>
                <div className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                  {formatCurrency(yearTotals.usd_expenses, 'USD')}
                </div>
              </motion.div>

              {/* PKR Income */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-green/10 to-green/5 rounded-2xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-4 h-4 text-green" />
                  <span className="text-footnote font-medium text-green">PKR Income</span>
                </div>
                <div className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                  {formatCurrency(yearTotals.pkr_income, 'PKR')}
                </div>
              </motion.div>

              {/* PKR Expenses */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-red/10 to-red/5 rounded-2xl p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="w-4 h-4 text-red" />
                  <span className="text-footnote font-medium text-red">PKR Expenses</span>
                </div>
                <div className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary">
                  {formatCurrency(yearTotals.pkr_expenses, 'PKR')}
                </div>
              </motion.div>
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Monthly History */}
      <motion.div variants={itemVariants} className="mt-6">
        <ContentCard>
          <div className="p-5 border-b border-black/[0.04] dark:border-white/[0.04]">
            <div className="flex items-center justify-between">
              <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                Monthly Pool History
              </h2>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-light-text-tertiary" />
                <span className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
                  Fiscal Month: 6th - 5th
                </span>
              </div>
            </div>
          </div>

          {poolHistory.length === 0 ? (
            <div className="text-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={springConfig}
                className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue/10 to-purple/10 flex items-center justify-center"
              >
                <Wallet className="w-10 h-10 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No pool entries for {selectedYear}
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-6">
                Add your first monthly entry to start tracking
              </p>
              <Button
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setIsAddModalOpen(true)}
              >
                Add Entry
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/[0.06] dark:border-white/[0.06]">
                    <th className="text-left py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      Month
                    </th>
                    <th className="text-right py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      USD Opening
                    </th>
                    <th className="text-right py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      USD Income
                    </th>
                    <th className="text-right py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      USD Expenses
                    </th>
                    <th className="text-right py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      USD Closing
                    </th>
                    <th className="text-right py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      PKR Closing
                    </th>
                    <th className="text-right py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      Rate
                    </th>
                    <th className="text-center py-4 px-5 text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      Actions
                    </th>
                  </tr>
                </thead>
                <motion.tbody
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {poolHistory.map((pool, index) => {
                    const netUsd = Number(pool.usd_income || 0) - Number(pool.usd_expenses || 0)
                    const isProfit = netUsd >= 0

                    return (
                      <motion.tr
                        key={`${pool.year}-${pool.month}`}
                        variants={tableRowVariants}
                        custom={index}
                        className="border-b border-black/[0.04] dark:border-white/[0.04] last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors group"
                      >
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full ${isProfit ? 'bg-green' : 'bg-red'}`} />
                            <span className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                              {pool.month} {pool.year}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-5 text-right text-body text-light-text-secondary dark:text-dark-text-secondary">
                          {formatCurrency(pool.usd_opening, 'USD')}
                        </td>
                        <td className="py-4 px-5 text-right">
                          <span className="text-body font-medium text-green">
                            +{formatCurrency(pool.usd_income, 'USD')}
                          </span>
                        </td>
                        <td className="py-4 px-5 text-right">
                          <span className="text-body font-medium text-red">
                            -{formatCurrency(pool.usd_expenses, 'USD')}
                          </span>
                        </td>
                        <td className="py-4 px-5 text-right">
                          <span className="text-body font-bold text-light-text-primary dark:text-dark-text-primary">
                            {formatCurrency(pool.usd_closing, 'USD')}
                          </span>
                        </td>
                        <td className="py-4 px-5 text-right">
                          <span className="text-body font-bold text-light-text-primary dark:text-dark-text-primary">
                            {formatCurrency(pool.pkr_closing, 'PKR')}
                          </span>
                        </td>
                        <td className="py-4 px-5 text-right">
                          <Badge variant="purple" size="sm">
                            {pool.exchange_rate}
                          </Badge>
                        </td>
                        <td className="py-4 px-5 text-center">
                          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => openEditModal(pool)}
                              className="p-2 rounded-xl hover:bg-blue/10 text-blue transition-colors"
                              title="Edit entry"
                            >
                              <Edit3 className="w-4 h-4" />
                            </motion.button>
                            <ChevronRight className="w-4 h-4 text-light-text-quaternary" />
                          </div>
                        </td>
                      </motion.tr>
                    )
                  })}
                </motion.tbody>
              </table>
            </div>
          )}
        </ContentCard>
      </motion.div>

      {/* Add Pool Entry Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false)
          resetForm()
        }}
        title="Add Monthly Pool Entry"
        size="lg"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleAddPool(); }} className="space-y-6">
          {/* Month & Year Selection */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Month"
              value={formData.month}
              onChange={(value) => setFormData({ ...formData, month: value })}
              options={MONTHS.map(m => ({ value: m, label: m }))}
              required
            />
            <Select
              label="Year"
              value={formData.year.toString()}
              onChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
              options={[
                { value: '2024', label: '2024' },
                { value: '2025', label: '2025' },
                { value: '2026', label: '2026' }
              ]}
              required
            />
          </div>

          {/* USD Section */}
          <div className="p-5 bg-gradient-to-br from-blue/10 to-blue/5 rounded-2xl">
            <h4 className="text-subheadline font-semibold text-blue mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              USD Pool
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="USD Income"
                type="number"
                step="0.01"
                value={formData.usd_income}
                onChange={(e) => setFormData({ ...formData, usd_income: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
              <Input
                label="USD Expenses"
                type="number"
                step="0.01"
                value={formData.usd_expenses}
                onChange={(e) => setFormData({ ...formData, usd_expenses: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* PKR Section */}
          <div className="p-5 bg-gradient-to-br from-green/10 to-green/5 rounded-2xl">
            <h4 className="text-subheadline font-semibold text-green mb-4 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              PKR Pool (Native)
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="PKR Income"
                type="number"
                value={formData.pkr_income}
                onChange={(e) => setFormData({ ...formData, pkr_income: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
              <Input
                label="PKR Expenses"
                type="number"
                value={formData.pkr_expenses}
                onChange={(e) => setFormData({ ...formData, pkr_expenses: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
          </div>

          {/* Exchange Rate */}
          <Input
            label="Exchange Rate (USD to PKR)"
            type="number"
            step="0.01"
            value={formData.exchange_rate}
            onChange={(e) => setFormData({ ...formData, exchange_rate: parseFloat(e.target.value) || 280 })}
            placeholder="280.00"
            icon={<TrendingUp className="w-4 h-4" />}
          />

          {/* Notes */}
          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Notes (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 rounded-2xl liquid-material-regular liquid-lens text-body text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all resize-none"
              rows={3}
              placeholder="Add any notes about this month's finances..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                setIsAddModalOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={saving}
              isLoading={saving}
            >
              Add Entry
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Pool Entry Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          resetForm()
        }}
        title={`Edit ${selectedPool?.month} ${selectedPool?.year} Entry`}
        size="lg"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleEditPool(); }} className="space-y-6">
          {/* USD Section */}
          <div className="p-5 bg-gradient-to-br from-blue/10 to-blue/5 rounded-2xl">
            <h4 className="text-subheadline font-semibold text-blue mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              USD Pool
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="USD Income"
                type="number"
                step="0.01"
                value={formData.usd_income}
                onChange={(e) => setFormData({ ...formData, usd_income: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
              <Input
                label="USD Expenses"
                type="number"
                step="0.01"
                value={formData.usd_expenses}
                onChange={(e) => setFormData({ ...formData, usd_expenses: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* PKR Section */}
          <div className="p-5 bg-gradient-to-br from-green/10 to-green/5 rounded-2xl">
            <h4 className="text-subheadline font-semibold text-green mb-4 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              PKR Pool (Native)
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="PKR Income"
                type="number"
                value={formData.pkr_income}
                onChange={(e) => setFormData({ ...formData, pkr_income: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
              <Input
                label="PKR Expenses"
                type="number"
                value={formData.pkr_expenses}
                onChange={(e) => setFormData({ ...formData, pkr_expenses: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
          </div>

          {/* Exchange Rate */}
          <Input
            label="Exchange Rate (USD to PKR)"
            type="number"
            step="0.01"
            value={formData.exchange_rate}
            onChange={(e) => setFormData({ ...formData, exchange_rate: parseFloat(e.target.value) || 280 })}
            placeholder="280.00"
            icon={<TrendingUp className="w-4 h-4" />}
          />

          {/* Notes */}
          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Notes (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 rounded-2xl liquid-material-regular liquid-lens text-body text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all resize-none"
              rows={3}
              placeholder="Add any notes about this month's finances..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                setIsEditModalOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={saving}
              isLoading={saving}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  )
}
